import { Controller } from "@hotwired/stimulus"

/**
 * Accordion Controller
 *
 * Handles expand/collapse functionality for accordion content blocks.
 * Supports single or multiple items open at once.
 *
 * Usage:
 *   <div data-controller="accordion" data-accordion-allow-multiple-value="false">
 *     <div class="accordion-item" data-accordion-target="item">
 *       <button data-action="click->accordion#toggle">Title</button>
 *       <div data-accordion-target="content" class="is-collapsed">Content</div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["item", "content"]
  static values = {
    allowMultiple: { type: Boolean, default: false }
  }

  connect() {
    // Initialize state based on existing classes
    this.itemTargets.forEach((item, index) => {
      const content = this.contentTargets[index]
      if (content && !content.classList.contains("is-collapsed")) {
        item.classList.add("is-expanded")
      }
    })
  }

  toggle(event) {
    const button = event.currentTarget
    const item = button.closest("[data-accordion-target='item']")

    if (!item) return

    const index = this.itemTargets.indexOf(item)
    if (index === -1) return

    const isExpanded = item.classList.contains("is-expanded")

    if (isExpanded) {
      this.collapse(index)
    } else {
      if (!this.allowMultipleValue) {
        this.collapseAll()
      }
      this.expand(index)
    }
  }

  expand(index) {
    const item = this.itemTargets[index]
    const content = this.contentTargets[index]
    const button = item?.querySelector("button")

    if (!item || !content) return

    item.classList.add("is-expanded")
    content.classList.remove("is-collapsed")

    if (button) {
      button.setAttribute("aria-expanded", "true")
    }
  }

  collapse(index) {
    const item = this.itemTargets[index]
    const content = this.contentTargets[index]
    const button = item?.querySelector("button")

    if (!item || !content) return

    item.classList.remove("is-expanded")
    content.classList.add("is-collapsed")

    if (button) {
      button.setAttribute("aria-expanded", "false")
    }
  }

  expandAll() {
    this.itemTargets.forEach((_, index) => this.expand(index))
  }

  collapseAll() {
    this.itemTargets.forEach((_, index) => this.collapse(index))
  }
}
